import React from 'react';
import { DateField as RADateField } from 'react-admin';
import { get } from 'lodash';

import TwoLinesLabel from 'components/common/TwoLinesLabel';

/**
 * Converts ISO date string (e.g., "2025-07-24T08:45:53.408Z") to UNIX timestamp
 * @param {string|Date} dateValue - ISO date string or Date object
 * @returns {number} UNIX timestamp in seconds
 */
const convertToUnixTimestamp = (dateValue: string | Date): number | null => {
  if (!dateValue) return null;

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

  // Check if date is valid
  if (isNaN(date.getTime())) return null;

  // Convert to UNIX timestamp (seconds since epoch)
  return Math.floor(date.getTime() / 1000);
};

const DateField = ({ withTimestamp = false, twoLines = false, withTime = false, ...props }) => {
  const { record, source, locales } = props;

  if (twoLines) {
    const value = get(record, source);

    if (value == null) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    const unixTimestamp = withTimestamp ? convertToUnixTimestamp(value) : null;

    return (
      <TwoLinesLabel
        primary={date.toLocaleDateString(locales, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hour12: true,
        })}
        secondary={withTimestamp && unixTimestamp ? `UNIX: ${unixTimestamp}` : ''}
        autoSize
      />
    );
  }

  return (
    <RADateField
      variant="body1"
      options={
        withTime
          ? {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              second: 'numeric',
              hour12: true,
            }
          : { year: 'numeric', month: 'short', day: 'numeric' }
      }
      {...props}
    />
  );
};

export default DateField;
